import { generateChunk } from '../../sim/chunkgen';
import type { Chunk, ChunkedTerrain } from '../../sim/chunks';

/**
 * Ruts and knocked cones are world state the bike changes as it rides, and the ruts change the ground the next
 * ride feels. A demo is recorded on untouched ground, so it must also play on untouched ground: this puts every
 * loaded chunk back to the ground as generated (heights regenerated bit-exact, not "height + rut", which rounds)
 * and returns the function that brings the player's ruts and cones back afterwards.
 */
/** Untouched heights of a chunk known ahead of time (the app regenerates ridden chunks in a worker). */
export type PristineSource = (ci: number, cj: number) => Float32Array | undefined;

export function pristineTerrain(terrain: ChunkedTerrain, known?: PristineSource): () => void {
  const saved: { c: Chunk; heights: Float32Array; rut: Float32Array }[] = [];
  // Chunks nobody has ridden yet are pristine now: their copy puts back whatever ruts the demo makes on them.
  const clean = new Map<Chunk, Float32Array>();
  const pristine = (c: Chunk) => clean.get(c) ?? known?.(c.ci, c.cj) ?? generateChunk(terrain.level, c.ci, c.cj).field.heights;
  const reset = (c: Chunk) => {
    terrain.touchChunk(c); // an open ground save (a ride's replay start) copies the ride's ground first
    c.field.heights.set(pristine(c));
    c.rut = null;
  };
  for (const c of terrain.loaded()) {
    if (!c.rut) { clean.set(c, c.field.heights.slice()); continue; }
    saved.push({ c, heights: c.field.heights.slice(), rut: c.rut });
    reset(c);
  }
  const field = terrain.field as unknown as { knocked?: Uint8Array };
  const knocked = field.knocked?.slice();
  field.knocked?.fill(0);
  // No terrain.version bump: the view rebuilds forest and scatter on one (seconds), and ruts never bump it either
  // (ChunkedTerrain.deform): the ground view follows chunk.rut / heights as it does for riding.
  return () => {
    for (const c of terrain.loaded()) if (c.rut) reset(c); // ruts the demo itself made
    for (const s of saved) { s.c.field.heights.set(s.heights); s.c.rut = s.rut; }
    if (knocked) field.knocked?.set(knocked);
  };
}
